import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  Menu,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Barcode from "react-barcode";
import { DndContext, DragEndEvent, useDroppable } from "@dnd-kit/core";
import { Rnd, DraggableData, ResizableDelta, Position } from "react-rnd";
import { useDraggable } from "@dnd-kit/core";
import { AvatarCarnet } from "../../components/AvatarCarnet";
import { useDispatch } from "react-redux";
import { getMarcoStore } from "../../store/files";

interface IDCardProps {
  marco: string;
  foto: string;
  nombre: string;
  cargo: string;
  documento: string;
  tipoSangre: string;
  empresa: string;
}
 
interface ItemState {
  x: number;
  y: number;
  width: number | string;
  height: number | string; 
  visible: boolean;
  card: 'front' | 'back' | 'toolbox';
  color?: string;
  fontSize?: number;
}

interface PositionsState {
  [key: string]: ItemState;
}

// --- Componente Droppable (para las tarjetas) ---
const Droppable: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { setNodeRef } = useDroppable({ id });

  return (
    <div ref={setNodeRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      {children}
    </div>
  );
};


export const IDCard: React.FC<IDCardProps> = ({
  marco,
  foto,
  nombre,
  cargo,
  documento,
  tipoSangre,
  empresa,
}) => {


  
  // DISPATCH 
  const dispatch = useDispatch();

  const initialItemsState: PositionsState = {
    foto: { x: 90, y: 20, width: 120, height: 120, visible: false, card: 'toolbox' },
    nombre: { x: 60, y: 160, width: 180, height: 28, visible: false, card: 'toolbox' },
    cargo: { x: 90, y: 190, width: 120, height: 20, visible: false, card: 'toolbox' },
    rh: { x: 80, y: 220, width: 50, height: 20, visible: false, card: 'toolbox' },
    codigoBarras: { x: 30, y: 350, width: 240, height: 60, visible: false, card: 'toolbox' },
    nombreEmpresa: { x: 60, y: 160, width: 280, height: 45, visible: false, card: 'toolbox' },
  };

  // Estado para manejar la posición, visibilidad y tarjeta de cada elemento
  const [items, setItems] = useState<PositionsState>(initialItemsState);

  // Estado para el menú contextual
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    itemId: string;
  } | null>(null);

  // Estado para las guías de alineación
  const [guides, setGuides] = useState({
    h: { visible: false, y: 0, card: '' },
    v: { visible: false, x: 0, card: '' },
  });


  const allItems = { foto, nombre, cargo, tipoSangre, documento, empresa };


  const [marcoFrontal, setMarcoFrontal] = useState('');
  const [marcoreverso, setMarcoReverso] = useState('');


  useEffect(() => {
     asignarMarcoFrontal();
  }, [documento]);


  useEffect(() => {
     asignarMarcoReverso();
  }, [documento]);



  const asignarMarcoFrontal = async() => {
    
    const result = await dispatch( getMarcoStore('frontal') );

    const base64 = await blobToBase64(result);
    setMarcoFrontal(base64);
        
     

  }


  const asignarMarcoReverso = async() => {
    
    const result = await dispatch( getMarcoStore('reverso') );
     
    const base64 = await blobToBase64(result);
    setMarcoReverso(base64);

  }




  const blobToBase64 = (blob: Blob | any): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!blob || !(blob instanceof Blob)) {
         resolve("");
         return;
      }
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  

  // Manejador para cuando se suelta un elemento DESDE LA TOOLBOX
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const id = active.id as string;
    
    // Si no se suelta sobre una tarjeta válida, no hacemos nada
    if (!over || (over.id !== 'front-card' && over.id !== 'back-card')) {
      return;
    }

    const newCard = over.id === 'front-card' ? 'front' : 'back';
    
    // Centrar el elemento en la tarjeta
    const cardWidth = 300;
    const itemState = items[id];
    const itemWidth = typeof itemState.width === 'number' ? itemState.width : 0;
    const newX = (cardWidth - itemWidth) / 2;

    setItems(prev => ({
      ...prev,
      [id]: { ...prev[id], card: newCard, visible: true, x: newX },
    }));
  };

  const handleContextMenu = (event: React.MouseEvent, itemId: string) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 30, mouseY: event.clientY + 40, itemId }
        : null
    );
  };

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleDeleteItem = () => {
    if (!contextMenu) return;
    // En lugar de solo ocultarlo, lo devolvemos a la toolbox
    setItems(prev => ({ 
      ...prev, 
      [contextMenu.itemId]: { ...prev[contextMenu.itemId], visible: false, card: 'toolbox' } 
    }));
    handleCloseContextMenu();
  };

  // Manejador para el arrastre en tiempo real para mostrar guías
  const handleDrag = (e: any, data: DraggableData, id: string, cardId: 'front' | 'back') => {
    const cardWidth = 300;
    const cardHeight = 450;
    const snapThreshold = 5; // Píxeles de sensibilidad para el snapping

    const itemState = items[id];
    const itemWidth = typeof itemState.width === 'number' ? itemState.width : 0;
    const itemHeight = typeof itemState.height === 'number' ? itemState.height : 0;

    let newX = data.x;
    let newY = data.y;

    const itemCenterX = newX + itemWidth / 2;
    const itemCenterY = newY + itemHeight / 2;

    const cardCenterX = cardWidth / 2;
    const cardCenterY = cardHeight / 2;

    let showVerticalGuide = false;
    let showHorizontalGuide = false;

    // Comprobar alineación vertical (guía horizontal)
    if (Math.abs(itemCenterY - cardCenterY) < snapThreshold) {
      newY = cardCenterY - itemHeight / 2; // Snap
      showHorizontalGuide = true;
    }

    // Comprobar alineación horizontal (guía vertical)
    if (Math.abs(itemCenterX - cardCenterX) < snapThreshold) {
      newX = cardCenterX - itemWidth / 2; // Snap
      showVerticalGuide = true;
    }

    // Actualizar el estado de las guías
    setGuides({
      h: { visible: showHorizontalGuide, y: cardCenterY, card: cardId },
      v: { visible: showVerticalGuide, x: cardCenterX, card: cardId },
    });

  };

  // Ocultar guías al detener el arrastre
  const handleDragStop = (e: any, d: DraggableData, id: string) => {
    setItems(prev => ({ ...prev, [id]: { ...prev[id], x: d.x, y: d.y } }));
    setGuides({ h: { visible: false, y: 0, card: '' }, v: { visible: false, x: 0, card: '' } });
  };



    // Componente para un item en la caja de herramientas
    const ToolboxItem: React.FC<{ id: string, label: string }> = ({ id, label }) => {
      const { attributes, listeners, setNodeRef, transform } = useDraggable({ id });
      const style = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 9999, // <-- Pone el elemento por encima de todo mientras se arrastra
      } : undefined;
  
      return (
        <ListItem
          ref={setNodeRef}
          style={style}
          {...listeners}
          {...attributes}
          sx={{
            cursor: 'grab',
            backgroundColor: 'action.hover',
            borderRadius: 1,
            mb: 1,
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <ListItemText primary={label} />
        </ListItem>
      );
    };

  return (
    <Box>
      <DndContext onDragEnd={handleDragEnd}>
        {/* Área imprimible */}
        <Box className="print-area">
          <Grid container spacing={4} justifyContent="center">
           

            {/* Caja de Herramientas */}
            <Grid size={{ xs: 12, md: 3  }}  className="no-print">
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Elementos</Typography>
                <List>
                  {Object.entries(items)
                    .filter(([id, item]) => !item.visible && item.card === 'toolbox').map(([id, item]) => (
                      <ToolboxItem 
                        key={id} 
                        id={id} 
                        label={id.charAt(0).toUpperCase() + id.slice(1)} 
                      />
                    )
                  )}
                </List>
              </Paper>
            </Grid>
            
            {/* Tarjeta frontal */}
            <Grid>
              <Card
                id="front-card"
                sx={{
                  width: 300,
                  height: 450,
                  borderRadius: 3,
                  overflow: "hidden",
                  position: "relative",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                }}
              >
                <Droppable id="front-card">
                  {/* Imagen de fondo */}
                  <img
                    src={marcoFrontal}
                    alt="Marco Frontal"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      zIndex: 0,
                    }}
                  />
                  {/* Guías de alineación */}
                  {guides.v.visible && guides.v.card === 'front' && (
                    <div style={{ position: 'absolute', left: guides.v.x, top: 0, width: '1px', height: '100%', backgroundColor: '#663cc1', zIndex: 99 }} />
                  )}
                  {guides.h.visible && guides.h.card === 'front' && (
                    <div style={{ position: 'absolute', top: guides.h.y, left: 0, height: '1px', width: '100%', backgroundColor: '#663cc1', zIndex: 99 }} />
                  )}

                  {Object.entries(items).map(([id, { x, y, width, height, visible, card }]) => {
                    if (!visible || card !== 'front') return null;
                    
                    let content;
                    switch (id) {
                      case 'foto':
                        content = <Box sx={{ width: '100%', height: '100%', borderRadius: "50%", overflow: "hidden", border: "6px solid #fff" }}><AvatarCarnet urlImage={allItems.foto} typeImage={"image-employee"} /></Box>;
                        break;
                      case 'nombre':
                        content = <Typography sx={{ fontWeight: "bold", color: items[id].color || "#003366", textAlign: 'center', fontSize: items[id].fontSize ? `${items[id].fontSize}px` : 'clamp(10px, 18cqi, 24px)' }}>{allItems.nombre}</Typography>;
                        break;
                      case 'cargo':
                        content = <Typography sx={{ color: items[id].color || "inherit", textAlign: 'center', fontSize: items[id].fontSize ? `${items[id].fontSize}px` : '12cqi' }}>{allItems.cargo}</Typography>;
                        break;
                      case 'rh':
                        content = <Typography sx={{ fontWeight: "bold", color: items[id].color || "black", textAlign: 'center', fontSize: items[id].fontSize ? `${items[id].fontSize}px` : '15cqi' }}>{allItems.tipoSangre}</Typography>;
                        break;
                      case 'codigoBarras':
                        content = <div style={{ backgroundColor: 'white', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Barcode value={allItems.documento} width={2} height={30} displayValue={true} fontSize={12} margin={5} /></div>;
                        break;
                      case 'nombreEmpresa':
                        content = <Typography sx={{ fontWeight: "bold", textAlign: "center", color: items[id].color || "#003366", fontSize: items[id].fontSize ? `${items[id].fontSize}px` : 'clamp(12px, 20cqi, 28px)' }}>{allItems.empresa}</Typography>;
                        break;
                      default:
                        return null;
                    }

                    const isPhoto = id === 'foto';

                    return (
                      <Rnd
                        key={id}
                        size={{ width: width, height: height }}
                        position={{ x, y }}
                        onDrag={(e, d) => handleDrag(e, d, id, 'front')}
                        onDragStop={(e, d) => handleDragStop(e, d, id)}
                        onResizeStop={(e, direction, ref, delta, position) => {
                          setItems(prev => ({
                            ...prev,
                            [id]: {
                              ...prev[id],
                              width: ref.offsetWidth,
                              height: ref.offsetHeight,
                              x: position.x,
                              y: position.y,
                            },
                          }));
                        }}
                        bounds="parent"
                        lockAspectRatio={isPhoto}
                        disableDragging={false}
                        enableResizing={true}
                        onContextMenu={(e: React.MouseEvent) => handleContextMenu(e, id)}
                      >
                        <div
                          className="print-item"
                          style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            userSelect: 'none', // Evita la selección de texto al arrastrar
                            containerType: 'inline-size', // Habilita las Container Queries
                          }}
                        > 
                          {content}
                        </div>
                      </Rnd>
                    );
                  })}
                </Droppable>
              </Card>
            </Grid>

            {/* Tarjeta trasera */}
            <Grid>
              <Card
                id="back-card"
                sx={{
                  width: 300,
                  height: 450,
                  borderRadius: 3,
                  overflow: "hidden",
                  position: "relative",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                }}
              >
                <Droppable id="back-card">
                  <img
                    src={marcoreverso}
                    alt="Marco Reverso"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      zIndex: 0,
                    }}
                  />
                  {/* Guías de alineación */}
                  {guides.v.visible && guides.v.card === 'back' && (
                    <div style={{ position: 'absolute', left: guides.v.x, top: 0, width: '1px', height: '100%', backgroundColor: '#663cc1', zIndex: 99 }} />
                  )}
                  {guides.h.visible && guides.h.card === 'back' && (
                    <div style={{ position: 'absolute', top: guides.h.y, left: 0, height: '1px', width: '100%', backgroundColor: '#663cc1', zIndex: 99 }} />
                  )}

                  {Object.entries(items).map(([id, { x, y, width, height, visible, card }]) => {
                    if (!visible || card !== 'back') return null;
                    
                    let content;
                    switch (id) {
                      case 'foto':
                        content = (
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: "50%",      // 🔹 Siempre redonda
                                    overflow: "hidden",
                                    border: "6px solid #fff", // 🔹 Borde fijo
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <AvatarCarnet
                                    urlImage={allItems.foto}
                                    typeImage="image-employee"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",    // 🔹 Mantiene círculo aunque cambie el tamaño
                                      objectFit: "cover",
                                    }}
                                  />
                                </Box>
                              );
                        break;
                      case 'nombre':
                        content = <Typography sx={{ fontWeight: "bold", color: items[id].color || "#003366", textAlign: 'center', fontSize: items[id].fontSize ? `${items[id].fontSize}px` : 'clamp(10px, 18cqi, 24px)' }}>{allItems.nombre}</Typography>;
                        break;
                      case 'cargo':
                        content = <Typography sx={{ color: items[id].color || "inherit", textAlign: 'center', fontSize: items[id].fontSize ? `${items[id].fontSize}px` : '12cqi' }}>{allItems.cargo}</Typography>;
                        break;
                      case 'rh':
                        content = <Typography sx={{ fontWeight: "bold", color: items[id].color || "black", textAlign: 'center', fontSize: items[id].fontSize ? `${items[id].fontSize}px` : '15cqi' }}>{allItems.tipoSangre}</Typography>;
                        break;
                      case 'codigoBarras':
                        content = <div style={{backgroundColor: 'white', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Barcode value={allItems.documento} width={2} height={30} displayValue={true} fontSize={12} margin={5} /></div>;
                        break;
                      case 'nombreEmpresa':
                        content = <Typography sx={{ fontWeight: "bold", textAlign: "center", color: items[id].color || "#003366", fontSize: items[id].fontSize ? `${items[id].fontSize}px` : 'clamp(12px, 20cqi, 28px)' }}>{allItems.empresa}</Typography>;
                        break;
                      default:
                        return null;
                    }

                    const isPhoto = id === 'foto';

                    return (
                      <Rnd
                        key={id}
                        size={{ width: width, height: height }}
                        position={{ x, y }}
                        onDrag={(e, d) => handleDrag(e, d, id, 'back')}
                        onDragStop={(e, d) => handleDragStop(e, d, id)}
                        onResizeStop={(e, direction, ref, delta, position) => {
                          setItems(prev => ({
                            ...prev,
                            [id]: {
                              ...prev[id],
                              width: ref.offsetWidth,
                              height: ref.offsetHeight,
                              x: position.x,
                              y: position.y,
                            },
                          }));
                        }}
                        bounds="parent"
                        lockAspectRatio={isPhoto}
                        enableResizing={isPhoto ? {
                            topLeft: true,
                            topRight: true,
                            bottomLeft: true,
                            bottomRight: true,
                          } : true
                        }
                        onContextMenu={(e: React.MouseEvent) => handleContextMenu(e, id)}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: isPhoto ? "50%" : 0, // Aplica borde redondo solo a la foto
                            overflow: "hidden",
                            containerType: 'inline-size', // Habilita las Container Queries
                            userSelect: 'none', // Evita la selección de texto al arrastrar
                          }}
                        >
                          {content}
                        </div>
                      </Rnd>
                    );
                  })}
                </Droppable>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DndContext>

      {/* Menú contextual */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleDeleteItem}>Borrar</MenuItem>
        {contextMenu !== null && contextMenu.itemId !== 'foto' && contextMenu.itemId !== 'codigoBarras' && (
          <MenuItem disableRipple>
            <label style={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer' }}>
              Color del texto
              <input 
                type="color" 
                value={items[contextMenu.itemId]?.color || '#000000'}
                style={{ marginLeft: '10px', cursor: 'pointer', border: 'none', background: 'none', height: '24px', width: '24px' }}
                onChange={(e) => {
                  setItems(prev => ({
                    ...prev,
                    [contextMenu.itemId]: { ...prev[contextMenu.itemId], color: e.target.value }
                  }));
                }}
              />
            </label>
          </MenuItem>
        )}
        {contextMenu !== null && contextMenu.itemId !== 'foto' && contextMenu.itemId !== 'codigoBarras' && (
          <MenuItem disableRipple>
            <label style={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer' }}>
              Tamaño del texto
              <input 
                type="number" 
                min="8" max="72"
                value={items[contextMenu.itemId]?.fontSize || ''}
                placeholder="Auto"
                style={{ marginLeft: '10px', width: '60px', padding: '2px', borderRadius: '4px', border: '1px solid #ccc' }}
                onChange={(e) => {
                  const val = e.target.value ? parseInt(e.target.value) : undefined;
                  setItems(prev => ({
                    ...prev,
                    [contextMenu.itemId]: { ...prev[contextMenu.itemId], fontSize: val }
                  }));
                }}
              />
            </label>
          </MenuItem>
        )}
      </Menu>

      {/* Botón de impresión */}
      <Box mt={4} textAlign="right" className="no-print">
        <Button variant="contained" color="primary" onClick={() => window.print()}>
          Imprimir
        </Button>
      </Box>

      {/* Estilos de impresión */}
      <style>
    {`
      @media print {
        /* Oculta todo por defecto */
        body * { 
          visibility: hidden;
        }
        /* Muestra el área de impresión y su contenido */
        .print-area, .print-area * { 
          visibility: visible;
        }
        /* Oculta las manijas de Rnd y otros elementos no imprimibles */
        .no-print {
          visibility: hidden !important;
          display: none !important;
        }
        /* Pero asegúrate de que el contenido que SÍ queremos imprimir (dentro de .no-print) sea visible */
        .print-item, .print-item * {
          visibility: visible !important;
        }
        .print-area {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          display: flex;
          justify-content: center;
          gap: 20px; /* espacio horizontal */
          page-break-after: avoid;
          page-break-before: avoid;
        }
        .print-area .MuiCard-root {
          page-break-inside: avoid;
          break-inside: avoid;
        }
      }
    `}
  </style>

    </Box>
  );
};
